import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, Observable } from 'rxjs';
import { BillingModule, Plan, Subscription } from '../../models/subscription';
import { SubscriptionService } from '../../../services/subscription.service';

/**
 * What each lab is on, and whether it has paid. A lab somebody created for themselves waits here
 * for approval and can do nothing until then. Invoicing is by bank transfer outside the platform;
 * marking a subscription paid here is what switches on the modules that wait for payment. Nothing
 * cuts a lab off automatically, so this list is where an overdue one gets noticed. Labs cannot
 * change plan themselves: they ask support, who do it here.
 */
@Component({
  selector: 'app-subscriptions',
  templateUrl: './subscriptions.component.html',
  styleUrls: ['./subscriptions.component.scss']
})
export class SubscriptionsComponent implements OnInit {

  subscriptions: Subscription[] = [];
  filter: 'PENDING' | 'UNPAID' | 'PAID' | 'ALL' = 'PENDING';
  overdueOnly = false;
  loading = true;
  error: string | null = null;
  busy: number | null = null;

  changing: Subscription | null = null;
  changeKind: 'FIXED' | 'FLEXIBLE' = 'FIXED';
  changePlan: string | null = null;
  changePeriod: 'MONTHLY' | 'YEARLY' = 'MONTHLY';
  changeModules: string[] = [];
  plans: Plan[] = [];
  modules: BillingModule[] = [];

  constructor(private subscriptionService: SubscriptionService, private snackBar: MatSnackBar) {}

  ngOnInit(): void {
    this.load();
  }

  get shown(): Subscription[] {
    return this.subscriptions
      .filter(each => this.filter === 'ALL'
        || (this.filter === 'PENDING' ? each.approvalStatus === 'PENDING' : each.paymentStatus === this.filter))
      .filter(each => !this.overdueOnly || each.overdue);
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.subscriptionService.getSubscriptions(null).subscribe({
      next: response => {
        this.subscriptions = response.items;
        this.loading = false;
      },
      error: () => {
        this.error = 'Could not load subscriptions.';
        this.loading = false;
      }
    });
  }

  approve(subscription: Subscription): void {
    this.act(subscription, this.subscriptionService.approve(subscription.id));
  }

  reject(subscription: Subscription): void {
    this.act(subscription, this.subscriptionService.reject(subscription.id));
  }

  startChange(subscription: Subscription): void {
    this.changing = subscription;
    this.changeKind = subscription.kind;
    this.changePlan = subscription.planCode;
    this.changePeriod = subscription.billingPeriod;
    this.changeModules = subscription.modules.map(each => each.featureFlag);
    if (!this.plans.length) {
      forkJoin([this.subscriptionService.getPlans(), this.subscriptionService.getModules()]).subscribe(([plans, modules]) => {
        this.plans = plans.items.filter(each => each.active && each.monthlyPrice !== null);
        this.modules = modules.items.filter(each => each.featureFlag && each.active && each.monthlyPrice !== null);
      });
    }
  }

  toggleChangeModule(flag: string, on: boolean): void {
    this.changeModules = on ? [...this.changeModules, flag] : this.changeModules.filter(each => each !== flag);
  }

  saveChange(): void {
    const subscription = this.changing as Subscription;
    this.busy = subscription.id;
    this.subscriptionService.change(subscription.tenantId, {
      kind: this.changeKind,
      planCode: this.changeKind === 'FIXED' ? this.changePlan : null,
      modules: this.changeKind === 'FLEXIBLE' ? this.changeModules : [],
      billingPeriod: this.changePeriod
    }).subscribe({
      next: () => {
        this.busy = null;
        this.changing = null;
        this.load();
      },
      error: err => {
        this.busy = null;
        this.snackBar.open(err?.error?.exceptionMessage || 'Could not change the plan', 'Close', { duration: 5000 });
      }
    });
  }

  markPaid(subscription: Subscription): void {
    this.act(subscription, this.subscriptionService.markPaid(subscription.id));
  }

  markUnpaid(subscription: Subscription): void {
    this.act(subscription, this.subscriptionService.markUnpaid(subscription.id));
  }

  renew(subscription: Subscription): void {
    this.act(subscription, this.subscriptionService.renew(subscription.id));
  }

  private act(subscription: Subscription, call: Observable<Subscription>): void {
    this.busy = subscription.id;
    call.subscribe({
      next: updated => {
        Object.assign(subscription, updated);
        this.busy = null;
      },
      error: err => {
        this.busy = null;
        this.snackBar.open(err?.error?.exceptionMessage || 'Could not save', 'Close', { duration: 5000 });
      }
    });
  }
}
